import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  UserCheck, 
  UserX, 
  Search, 
  CheckCircle2, 
  XCircle,
  Download,
  TrendingUp,
  DollarSign,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../../lib/api-client';
import { PaginationWrapper } from '../ui/PaginationWrapper';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  referralCode: string;
  isAffiliateEnabled: boolean;
  affiliateEnabledAt?: string;
  createdAt: string;
}

interface AffiliateStats {
  totalAffiliates: number;
  activeAffiliates: number;
  pendingRequests: number;
  totalClicks: number;
  totalCommissions: number;
}

export function AdminAffiliateManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<AffiliateStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'enabled' | 'disabled'>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [actionType, setActionType] = useState<'enable' | 'disable' | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, [currentPage, filter, searchQuery]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        search: searchQuery,
        affiliateFilter: filter
      });

      const response = await apiClient.get(`/admin/users/affiliate-list?${params}`);
      if (response.data.success) {
        setUsers(response.data.users);
        setTotalPages(response.data.totalPages);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/admin/affiliate/stats');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleToggleAffiliate = async (user: User, enable: boolean) => {
    try {
      const response = await apiClient.post('/admin/users/toggle-affiliate', {
        userId: user._id,
        enable
      });

      if (response.data.success) {
        toast.success(
          enable 
            ? `Affiliate access enabled for ${user.firstName} ${user.lastName}` 
            : `Affiliate access disabled for ${user.firstName} ${user.lastName}`
        );
        
        // Update users state locally instead of refetching
        setUsers(prevUsers => 
          prevUsers.map(u => 
            u._id === user._id 
              ? { 
                  ...u, 
                  isAffiliateEnabled: enable,
                  affiliateEnabledAt: enable ? new Date().toISOString() : null
                } 
              : u
          )
        );
        
        // Update stats locally
        setStats(prevStats => prevStats ? {
          ...prevStats,
          totalAffiliates: enable ? prevStats.totalAffiliates + 1 : prevStats.totalAffiliates - 1,
          activeAffiliates: enable ? prevStats.activeAffiliates + 1 : prevStats.activeAffiliates - 1
        } : null);
        
        setSelectedUser(null);
        setActionType(null);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update affiliate status');
    }
  };

  const filteredUsers = users;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold mb-2">Affiliate Management</h2>
        <p className="text-gray-600">
          Enable or disable affiliate access for users
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Affiliates</p>
                  <p className="text-2xl font-bold">{stats.totalAffiliates}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Now</p>
                  <p className="text-2xl font-bold">{stats.activeAffiliates}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Clicks</p>
                  <p className="text-2xl font-bold">{stats.totalClicks}</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <TrendingUp className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Commissions</p>
                  <p className="text-2xl font-bold">₹{stats.totalCommissions}</p>
                </div>
                <div className="p-3 bg-rose-100 rounded-full">
                  <DollarSign className="h-6 w-6 text-rose-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters & Search */}
      <Card>
        <CardHeader>
          <CardTitle>User List</CardTitle>
          <CardDescription>Search and manage affiliate access</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, email, or referral code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex gap-2">
              <Button
                variant={filter === 'all' ? 'default' : 'outline'}
                onClick={() => setFilter('all')}
              >
                All Users
              </Button>
              <Button
                variant={filter === 'enabled' ? 'default' : 'outline'}
                onClick={() => setFilter('enabled')}
                className="bg-green-600 hover:bg-green-700"
              >
                Enabled
              </Button>
              <Button
                variant={filter === 'disabled' ? 'default' : 'outline'}
                onClick={() => setFilter('disabled')}
              >
                Disabled
              </Button>
            </div>
          </div>

          {/* Users Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">User</th>
                  <th className="text-left p-3">Referral Code</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Enabled Date</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="text-center p-8">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-500" />
                      </div>
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center p-8 text-gray-500">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user._id} className="border-b hover:bg-gray-50">
                      <td className="p-3">
                        <div>
                          <p className="font-medium">
                            {user.firstName} {user.lastName}
                          </p>
                          <p className="text-sm text-gray-500">{user.email}</p>
                        </div>
                      </td>
                      <td className="p-3">
                        <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                          {user.referralCode || 'Not generated'}
                        </code>
                      </td>
                      <td className="p-3">
                        {user.isAffiliateEnabled ? (
                          <Badge className="bg-green-100 text-green-700 border-green-300">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Enabled
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-600">
                            <XCircle className="h-3 w-3 mr-1" />
                            Disabled
                          </Badge>
                        )}
                      </td>
                      <td className="p-3 text-sm text-gray-600">
                        {user.affiliateEnabledAt
                          ? new Date(user.affiliateEnabledAt).toLocaleDateString()
                          : '-'}
                      </td>
                      <td className="p-3 text-right">
                        {user.isAffiliateEnabled ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => {
                              setSelectedUser(user);
                              setActionType('disable');
                            }}
                          >
                            <UserX className="h-4 w-4 mr-1" />
                            Disable
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => {
                              setSelectedUser(user);
                              setActionType('enable');
                            }}
                          >
                            <UserCheck className="h-4 w-4 mr-1" />
                            Enable
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <PaginationWrapper
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <AlertDialog open={!!selectedUser && !!actionType} onOpenChange={() => {
        setSelectedUser(null);
        setActionType(null);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {actionType === 'enable' ? 'Enable Affiliate Access' : 'Disable Affiliate Access'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {actionType === 'enable' ? (
                <>
                  Are you sure you want to enable affiliate access for{' '}
                  <strong>{selectedUser?.firstName} {selectedUser?.lastName}</strong>?
                  <br /><br />
                  They will be able to:
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Download WordPress plugin</li>
                    <li>Earn commissions from referrals</li>
                    <li>Access affiliate dashboard</li>
                  </ul>
                </>
              ) : (
                <>
                  Are you sure you want to disable affiliate access for{' '}
                  <strong>{selectedUser?.firstName} {selectedUser?.lastName}</strong>?
                  <br /><br />
                  They will lose access to:
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>WordPress plugin downloads</li>
                    <li>Affiliate dashboard</li>
                    <li>Commission earnings (existing commissions preserved)</li>
                  </ul>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedUser && handleToggleAffiliate(selectedUser, actionType === 'enable')}
              className={actionType === 'enable' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {actionType === 'enable' ? 'Enable Affiliate' : 'Disable Affiliate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}